import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react"
import { Platform } from "react-native"

const STORAGE_KEY = "brevet_funnel_state_v1"

export const BREVET_FUNNEL_INITIAL = {
    funnelStateId: null,
    // Pré-rempli depuis la LP (query params ?firstname=&class=&email=&ref=)
    studentFirstname: "",
    studentClass: null,
    parentEmail: "",
    // Code d'affiliation capté à l'arrivée (?ref=XXXX) — applique -50% au paiement
    referralCode: null,
    referrerFirstname: null,
    // Écran 1/4 — diag matières + modules + objectif
    subjects: [],
    selectedModules: [],
    objective: null,
    // Écran 2/4 — diag profond (chips/cards inspirés du pattern onboarding V2)
    brevetBlanc: null,
    niveauActuel: null,
    autonomie: null,
    besoinsSpeciaux: [],
    interets: "",
    timePerWeekend: null,
    // Écran 3/4 — reco (formule retenue par la pure fn)
    selectedFormulaId: null,
    // Écran 4/4 — pricing + créneau + Stripe (fusion)
    selectedSlotId: null,
    paymentIntentId: null,
    paid: false,
}

const loadFromStorage = () => {
    if (Platform.OS !== "web") return null
    try {
        const raw = window?.sessionStorage?.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch (_e) {
        return null
    }
}

const saveToStorage = (state) => {
    if (Platform.OS !== "web") return
    try {
        window?.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (_e) {
        /* noop */
    }
}

const clearStorage = () => {
    if (Platform.OS !== "web") return
    try {
        window?.sessionStorage?.removeItem(STORAGE_KEY)
    } catch (_e) {
        /* noop */
    }
}

const BrevetFunnelContext = createContext(null)

/**
 * Provider unique pour le funnel Brevet — source de vérité du state cross-écrans.
 * Persiste sur sessionStorage (web only) pour survivre aux remounts entre écrans
 * et reprendre le funnel après un refresh.
 *
 * Sur native, le state est en mémoire uniquement (la session funnel est courte
 * et redémarre depuis la LP en cas de relance).
 */
export const BrevetFunnelProvider = ({ children }) => {
    const [state, setState] = useState(() => ({
        ...BREVET_FUNNEL_INITIAL,
        ...(loadFromStorage() || {}),
    }))

    const update = useCallback((patch) => {
        setState((prev) => {
            const next = { ...prev, ...patch }
            // Persist inline pour éviter la double-écriture via useEffect.
            saveToStorage(next)
            return next
        })
    }, [])

    const reset = useCallback(() => {
        clearStorage()
        setState(BREVET_FUNNEL_INITIAL)
    }, [])

    const value = useMemo(() => ({ state, update, reset }), [state, update, reset])

    return (
        <BrevetFunnelContext.Provider value={value}>
            {children}
        </BrevetFunnelContext.Provider>
    )
}

/**
 * Hook de consommation — chaque écran lit/écrit dans la même source de vérité.
 * Hors Provider, retourne un fallback safe pour ne pas casser un éventuel screenshot/preview.
 */
export const useBrevetFunnel = () => {
    const ctx = useContext(BrevetFunnelContext)
    if (ctx) return ctx
    // Fallback — ne devrait pas arriver en runtime, mais protège les tests/previews.
    return {
        state: BREVET_FUNNEL_INITIAL,
        update: () => {},
        reset: () => {},
    }
}

/**
 * HOC pour wrapper un screen avec le Provider (utilisé dans navigationScreens.js,
 * où chaque route est listée à plat et n'a pas de parent commun).
 */
export const withBrevetFunnelProvider = (Component) => {
    const Wrapped = (props) => (
        <BrevetFunnelProvider>
            <Component {...props} />
        </BrevetFunnelProvider>
    )
    Wrapped.displayName = `withBrevetFunnelProvider(${
        Component.displayName || Component.name || "Component"
    })`
    return Wrapped
}
